/**
 * Referral partner catalogue helpers (D-057 simplified portal).
 *
 * The partner makes NO economic choice: rates, benefits and eligibility are
 * standardized by AllDrivers. This module only resolves the single approved
 * offer used to create a campaign, formats authoritative values, and validates
 * the hosted Stripe onboarding URL.
 */

export interface Offer {
  id: number; name: string; product: string; interval: string; benefit: string;
  benefit_days: number; annual_fraction: string | null; starts_at: string; ends_at: string | null;
  rate_bps: number; base_mode: string; maturity_days: number; currency: string;
}

/**
 * The single standard offer a partner's campaign is created under.
 *
 * Deterministic and never a user choice: a lone published offer wins; otherwise
 * the lowest-id offer covering every product (`product === 'all'`); otherwise
 * `null` (the caller fails closed and shows an honest unavailable state instead
 * of guessing economics).
 */
export function standardOffer(offers: Offer[]): Offer | null {
  if (!offers || offers.length === 0) return null;
  if (offers.length === 1) return offers[0];
  const all = offers.filter(offer => offer.product === 'all').sort((a, b) => a.id - b.id);
  return all[0] ?? null;
}

/** Locale-aware percentage from authoritative basis points (never a constant). */
export const formatRate = (bps: number, locale?: string) =>
  `${new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(bps / 100)}%`;

/** Locale-aware integer minor-unit money value, e.g. 1234 EUR -> "12,34 €". */
export const formatMoney = (cents: number, currency: string, locale?: string) => {
  const value = (Number.isFinite(cents) ? cents : 0) / 100;
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency', currency: (currency || 'eur').toUpperCase(),
    }).format(value);
  } catch {
    return `${value.toFixed(2)} ${(currency || 'eur').toUpperCase()}`;
  }
};

/** Hosted Stripe onboarding links are only ever navigated when they are HTTPS on Stripe's Connect host. */
export function safeStripeUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  try {
    const url = new URL(value);
    const ok = url.protocol === 'https:' && url.hostname === 'connect.stripe.com' && !url.username && !url.password && !url.port;
    return ok ? url.toString() : null;
  } catch { return null; }
}

/**
 * The server-composed share link, accepted only as a plain HTTP(S) URL without
 * credentials whose sole query is `ref=<the current code>`. A link that does not
 * carry exactly the code being shown (stale or foreign) is never offered.
 */
export function safeShareUrl(value: unknown, code: string | null | undefined): string | null {
  if (typeof value !== 'string' || !code) return null;
  const match = /^https?:\/\/[^\s/?#@\\]+\/[^\s?#@\\]*\?ref=([A-Z0-9]{4,32})$/.exec(value);
  return match && match[1] === code ? value : null;
}

/** Referral codes are letters/digits only; the server is the authority. */
export const normalizeReferralCode = (value: string) => (value || '').trim().toUpperCase();
export const isReferralCodeShape = (value: string) => /^[A-Z0-9]{4,32}$/.test(normalizeReferralCode(value));
